import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SliceTransition from '../components/transitions/SliceTransition';
import WebUIChatInterface from '../components/chat/webui/WebUIChatInterface';

const Agents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  // Agent color scheme for UI differentiation
  const getAgentColor = (agentId) => {
    const colors = {
      'chatty-ai': { primary: '#3b82f6', secondary: '#dbeafe', accent: '#1d4ed8' },
      'project-manager': { primary: '#059669', secondary: '#d1fae5', accent: '#047857' },
      'accounting-assistant': { primary: '#dc2626', secondary: '#fee2e2', accent: '#b91c1c' },
      'compliance-advisor': { primary: '#7c3aed', secondary: '#ede9fe', accent: '#5b21b6' }
    };
    return colors[agentId] || colors['chatty-ai'];
  };
  // eslint-disable-next-line no-unused-vars
  const [isTransitioning, setIsTransitioning] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showContent, setShowContent] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [showOverlay, setShowOverlay] = useState(false);

  // Get user membership level
  const userMembership = user?.['custom:membership'] || 'standard';
  
  // Agent configuration with membership-based access
  const agentCategories = [
    {
      category: "AI Assistants",
      agents: [
        {
          id: 'chatty-ai',
          name: 'Chatty AI Assistant',
          description: 'Your personal AI companion for questions, coding help, creative writing, and general assistance. Free for all registered users.',
          icon: '🤖',
          tags: ['AI', 'Chat', 'Assistant', 'Free'],
          status: 'available',
          price: 'Free',
          url: '/agents/chatty',
          standalone: false,
          access: 'all-users'
        }
      ]
    },
    {
      category: "Technical Specialists",
      agents: [
        {
          id: 'project-manager',
          name: 'Project Manager',
          description: 'Intelligent project management with task automation and team coordination',
          icon: '📋',
          tags: ['Project Management', 'Team Coordination', 'Automation'],
          status: 'available',
          price: '$49/month',
          url: null,
          standalone: false
        },
        {
          id: 'ai-coder',
          name: 'AI Coding Assistant',
          description: 'Professional coding companion with code generation and review',
          icon: '💻',
          tags: ['AI', 'Coding', 'Development'],
          status: 'coming-soon',
          price: '$39/month',
          url: null,
          standalone: true
        }
      ]
    },
    {
      category: "Business Specialists",
      agents: [
        {
          id: 'accounting-assistant',
          name: 'Accounting Assistant',
          description: 'Automated bookkeeping and financial advisory services',
          icon: '📊',
          tags: ['Finance', 'Accounting', 'Business'],
          status: 'coming-soon',
          price: '$79/month',
          url: null,
          standalone: true
        },
        {
          id: 'real-estate-agent',
          name: 'Real Estate Assistant',
          description: 'Property analysis and market insights for real estate',
          icon: '🏠',
          tags: ['Real Estate', 'Analysis', 'Market'],
          status: 'coming-soon',
          price: '$59/month',
          url: null,
          standalone: true
        }
      ]
    },
    {
      category: "Legal & Compliance",
      agents: [
        {
          id: 'compliance-advisor',
          name: 'Compliance Advisor',
          description: 'Contract review and legal document assistance',
          icon: '⚖️',
          tags: ['Legal', 'Contracts', 'Compliance'],
          status: 'coming-soon',
          price: '$99/month',
          url: null,
          standalone: true
        }
      ]
    }
  ];

  // Determine user's agent access level
  const getAgentAccess = (agent) => {
    // Chatty AI is free for all registered users
    if (agent.id === 'chatty-ai') {
      return { hasAccess: true, accessType: 'included', reason: 'Free for all registered users' };
    }
    
    // Full access tiers: sales, admin, owner, advanced
    const fullAccessTiers = ['sales', 'admin', 'owner', 'advanced'];
    
    if (fullAccessTiers.includes(userMembership)) {
      return { hasAccess: true, accessType: 'included', reason: 'Included in your plan' };
    }
    
    // Pro users get one free agent (implement selection logic later)
    if (userMembership === 'pro') {
      return { hasAccess: false, accessType: 'select-one', reason: 'Choose 1 free agent (Pro plan)' };
    }
    
    // Standard users can purchase individually
    if (userMembership === 'standard') {
      return { hasAccess: false, accessType: 'purchase', reason: `Purchase for ${agent.price}` };
    }
    
    return { hasAccess: false, accessType: 'upgrade', reason: 'Upgrade plan to access' };
  };

  const handleAgentClick = (agent) => {
    const access = getAgentAccess(agent);
    
    if (agent.status === 'coming-soon') {
      alert('This agent is coming soon! Stay tuned for updates.');
      return;
    }

    if (!access.hasAccess) {
      if (access.accessType === 'purchase') {
        alert(`Purchase this agent for ${agent.price} to access it.`);
      } else if (access.accessType === 'select-one') {
        alert('As a Pro user, you can choose 1 free agent. Contact support to make your selection.');
      } else {
        alert('Upgrade to Advanced plan or higher to access all agents.');
      }
      return;
    }

    // User has access - launch the agent
    if (agent.standalone && agent.url) {
      // For standalone agents, open in new window/iframe
      window.open(agent.url, '_blank');
    } else {
      // ✅ ALL AGENTS: Navigate to real Chatty with agent context
      navigate('/chatty', { 
        state: { 
          agentMode: true,
          agentConfig: {
            id: agent.id,
            name: agent.name,
            icon: agent.icon,
            description: agent.description,
            tags: agent.tags,
            color: getAgentColor(agent.id) // Add distinctive color
          }
        }
      });
    }
  };

  // Handle slice transition completion
  const handleTransitionComplete = () => {
    // Perform any cleanup or preparation here
    // console.log('🚀 Slice transition complete - preparing WebUI interface...');
    
    // Navigate to the new interface after transition
    setTimeout(() => {
      navigate('/agents/chatty');
    }, 500);
  };

  const AgentCard = ({ agent }) => {
    const access = getAgentAccess(agent);
    
    return (
      <div 
        className={`rounded-lg p-6 cursor-pointer apple-smooth-hover border ${
          agent.status === 'coming-soon' || !access.hasAccess ? 'opacity-75' : ''
        }`}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--text-secondary)',
          borderWidth: '1px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
        }}
        onClick={() => handleAgentClick(agent)}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <span className="text-4xl mr-3">{agent.icon}</span>
            <div>
              <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{agent.name}</h3>
              <div className="flex gap-2 mt-1">
                <span className={`text-sm px-2 py-1 rounded ${
                  agent.status === 'available' 
                    ? 'bg-green-600 text-green-100' 
                    : 'bg-yellow-600 text-yellow-100'
                }`}>
                  {agent.status === 'available' ? 'Available' : 'Coming Soon'}
                </span>
                
                {/* Access status badge */}
                <span className={`text-sm px-2 py-1 rounded ${
                  access.hasAccess 
                    ? 'bg-blue-600 text-blue-100' 
                    : access.accessType === 'purchase'
                    ? 'bg-purple-600 text-purple-100'
                    : access.accessType === 'select-one'
                    ? 'bg-orange-600 text-orange-100'
                    : 'bg-red-600 text-red-100'
                }`}>
                  {access.hasAccess ? 'Included' : 
                   access.accessType === 'purchase' ? agent.price :
                   access.accessType === 'select-one' ? 'Pro Choice' : 'Upgrade'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <p style={{ color: 'var(--text-secondary)' }} className="mb-4">{agent.description}</p>
        
        <div className="flex flex-wrap gap-2 mb-3">
          {agent.tags.map((tag, index) => (
            <span 
              key={index}
              className="bg-blue-600 text-blue-100 text-xs px-2 py-1 rounded"
            >
              {tag}
            </span>
          ))}
        </div>
        
        {/* Access reason */}
        <div className="text-sm italic" style={{ color: 'var(--text-secondary)' }}>
          {access.reason}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full overflow-auto">
      <div className="px-6 py-6 transition-all duration-300 min-h-screen"
        style={{
          backgroundColor: 'transparent',
          color: 'var(--text-primary)',
          paddingTop: 'calc(var(--navbar-height) + 1.5rem)', // Adds navbar height + existing py-6 (1.5rem)
        }}>
        {showContent && (
          <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">AI Agents & Specialists</h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Access powerful AI agents and specialized tools to enhance your productivity
          </p>
          
          {/* Membership Status */}
          <div className="mt-4 backdrop-blur-sm border border-blue-400/50 rounded-lg px-2 py-2 sm:px-4 transition-all duration-300 hover:border-purple-300/60"
               style={{
                 background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)'
               }}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Current Plan: <span className="text-blue-400 capitalize">{userMembership}</span>
                </h3>
                <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {userMembership === 'standard' && 'Purchase agents individually or upgrade to Advanced for all agents'}
                  {userMembership === 'pro' && 'Choose 1 free agent or upgrade to Advanced for all agents'}
                  {['advanced', 'sales', 'admin', 'owner'].includes(userMembership) && 'You have maximum access - all agents included'}
                </p>
              </div>
              {!['advanced', 'sales', 'admin', 'owner'].includes(userMembership) ? (
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 sm:px-4 sm:py-2 rounded transition text-xs sm:text-base whitespace-nowrap flex items-center justify-center"
                  onClick={() => navigate('/account')}
                >
                  Upgrade
                </button>
              ) : (
                <div className="bg-green-600 text-white w-8 h-8 sm:w-auto sm:h-auto sm:px-4 sm:py-2 rounded font-semibold text-xs sm:text-base whitespace-nowrap flex items-center justify-center">
                  <span className="sm:hidden">✨</span>
                  <span className="hidden sm:inline">✨ Premium</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Agent Categories */}
        {agentCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-10">
            <h2 className="text-2xl font-semibold mb-6 text-blue-400">
              {category.category}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.agents.map((agent, agentIndex) => (
                <AgentCard key={agentIndex} agent={agent} />
              ))}
            </div>
          </div>
        ))}

        {/* Membership Benefits */}
        <div className="mt-10 p-6 rounded-lg" 
          style={{ 
            backgroundColor: 'var(--bg-secondary)', 
            boxShadow: 'var(--shadow-box)'
          }}>
          <h3 className="text-xl font-semibold text-blue-400 mb-4">Membership Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="text-center">
              <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Standard</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Purchase agents individually</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Pro</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Choose 1 free agent</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Advanced+</h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>All agents included</p>
            </div>
          </div>
        </div>
        </div>
        )}
      </div>
      
      {/* Theme-aware overlay that fades in when card is clicked */}
      <div 
        className={`fixed inset-0 transition-opacity duration-1000 ease-in ${showOverlay ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ 
          backgroundColor: theme === 'dark' ? '#000000' : '#ffffff',
          zIndex: 1000 
        }}
      />
      
      {/* Slice Transition Overlay */}
      <SliceTransition 
        isActive={isTransitioning}
        onComplete={handleTransitionComplete}
        targetComponent={<WebUIChatInterface />}
        duration={2500}
      />
    </div>
  );
};

export default Agents;